import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createRecruitmentApi,
  deleteRecruitmentApi,
  fetchRecruitmentById,
  fetchRecruitments,
  publishRecruitmentApi,
  updateRecruitmentApi,
} from "../services/recruitmentsService";

const RECRUITMENTS_QUERY_KEY = ["recruitments"];

export const useRecruitments = () =>
  useQuery({
    queryKey: RECRUITMENTS_QUERY_KEY,
    queryFn: fetchRecruitments,
  });

export const useRecruitment = (id, options = {}) =>
  useQuery({
    queryKey: [...RECRUITMENTS_QUERY_KEY, id],
    queryFn: () => fetchRecruitmentById(id),
    enabled: Boolean(id),
    ...options,
  });

export const useCreateRecruitment = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createRecruitmentApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RECRUITMENTS_QUERY_KEY });
      toast.success("Offre d'emploi bien ajoutée", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Offre d'emploi non ajoutée", { autoClose: 3000 });
    },
  });
};

export const useUpdateRecruitment = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => updateRecruitmentApi(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: RECRUITMENTS_QUERY_KEY });
      if (variables?.id) {
        qc.invalidateQueries({
          queryKey: [...RECRUITMENTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Offre d'emploi bien modifiée", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Offre d'emploi non modifiée", { autoClose: 3000 });
    },
  });
};

export const useDeleteRecruitment = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: deleteRecruitmentApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RECRUITMENTS_QUERY_KEY });
      toast.success("Offre d'emploi supprimée avec succès", {
        autoClose: 3000,
      });
    },
    onError: () => {
      toast.error(
        "Une erreur est survenue lors de la suppression de l'offre d'emploi",
        { autoClose: 3000 }
      );
    },
  });
};

export const usePublishRecruitment = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id) => publishRecruitmentApi(id),
    onSuccess: (_data, recruitmentId) => {
      qc.invalidateQueries({ queryKey: RECRUITMENTS_QUERY_KEY });
      if (recruitmentId) {
        qc.invalidateQueries({
          queryKey: [...RECRUITMENTS_QUERY_KEY, recruitmentId],
        });
      }
      toast.success("Offre d'emploi bien validée", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Offre d'emploi non validée", { autoClose: 3000 });
    },
  });
};
